import { Link } from "react-router-dom";
import { ShieldCheck, Copy, Info } from "lucide-react";
import { toast } from "sonner";

const DEMO_EMAIL = "demo@confirmph.app";
const DEMO_PASSWORD = "demo1234";

async function copyValue(value, label) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied`, { duration: 1800 });
  } catch {
    toast.error("Could not copy: please copy manually");
  }
}

/**
 * Demo credentials card with click-to-copy + sandbox explainer.
 * Shown on the business Login and Register pages so prospects can try the
 * preview account in one tap. Variant `withLink` adds an inline link back to
 * the login page (useful on Register).
 */
export default function BusinessDemoCard({ showLoginLink = false }) {
  return (
    <div
      data-testid="business-demo-card"
      className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50/70 p-4"
    >
      <div className="flex items-center gap-2 text-emerald-900">
        <ShieldCheck className="w-4 h-4" />
        <div className="text-sm font-semibold">
          Try the live demo: Dumaguete Island Wheels
        </div>
      </div>
      <p className="text-xs text-emerald-900/80 mt-1.5 leading-relaxed">
        A full preview account with vehicles, bookings, problems, and finance records. Click to copy{" "}
        {showLoginLink ? (
          <>and paste on the <Link to="/login?as=business" data-testid="business-demo-card-login-link" className="underline hover:no-underline">business login</Link> page.</>
        ) : (
          "and paste into the form above."
        )}
      </p>
      <div className="mt-3 grid sm:grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => copyValue(DEMO_EMAIL, "Email")}
          data-testid="demo-copy-email"
          className="group flex items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-white px-3 py-2 text-left hover:border-emerald-400 transition"
        >
          <span className="min-w-0">
            <span className="block text-[10px] uppercase tracking-[0.18em] text-emerald-800 font-semibold">Email</span>
            <span className="block text-sm font-mono text-slate-900 truncate">{DEMO_EMAIL}</span>
          </span>
          <Copy className="w-4 h-4 text-emerald-700 group-hover:scale-110 transition flex-shrink-0" />
        </button>
        <button
          type="button"
          onClick={() => copyValue(DEMO_PASSWORD, "Password")}
          data-testid="demo-copy-password"
          className="group flex items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-white px-3 py-2 text-left hover:border-emerald-400 transition"
        >
          <span className="min-w-0">
            <span className="block text-[10px] uppercase tracking-[0.18em] text-emerald-800 font-semibold">Password</span>
            <span className="block text-sm font-mono text-slate-900 truncate">{DEMO_PASSWORD}</span>
          </span>
          <Copy className="w-4 h-4 text-emerald-700 group-hover:scale-110 transition flex-shrink-0" />
        </button>
      </div>
      <div className="mt-3 flex items-start gap-2 text-xs text-emerald-900/80" data-testid="demo-sandbox-explainer">
        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span>
          This is a <strong>sandbox</strong>: feel free to add, edit, or delete anything. Real emails and payments are mocked, and your changes
          reset to the canonical demo state every time you log in. Nothing you do here is sent to a real customer.
        </span>
      </div>
    </div>
  );
}
